import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Plus, X, Music, Trash2 } from 'lucide-react';

const getCategories = (couple) => {
  const p1Name = couple?.person1Name || 'Person 1';
  return [
    { id: 'getting-ready', label: 'Getting Ready' },
    { id: 'ceremony-arrival', label: 'Guesteankomst' },
    { id: 'processional', label: 'Procession' },
    { id: 'entrance', label: `${p1Name}s inngang` },
    { id: 'ceremony', label: 'Ceremony / Signing' },
    { id: 'exit', label: 'Exit' },
    { id: 'cocktail', label: 'Cocktail Hour' },
    { id: 'dinner', label: 'Middag' },
    { id: 'first-dance', label: 'First dance' },
    { id: 'party', label: 'Fest' },
  ];
};

const SongEntry = ({ song, onUpdate, onRemove }) => (
  <div className="flex items-center gap-2 bg-stone-50 rounded-lg px-2.5 py-1.5 group">
    <Music className="w-3 h-3 text-stone-400 flex-shrink-0" />
    <input value={song.title} onChange={e => onUpdate({ ...song, title: e.target.value })} placeholder="Sang / artist"
      className="flex-1 text-xs font-sans bg-transparent focus:outline-none text-stone-700 placeholder:text-stone-300 min-w-0" />
    <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 transition-opacity">
      <X className="w-3 h-3" />
    </button>
  </div>
);

const PlaylistCategory = ({ category, songs, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const addSong = () => onUpdate([...songs, { title: '' }]);
  const removeSong = (idx) => onUpdate(songs.filter((_, i) => i !== idx));
  const updateSong = (idx, s) => onUpdate(songs.map((item, i) => i === idx ? s : item));

  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-stone-50 transition-colors">
        <span className="text-sm font-sans font-medium text-stone-700">{category.label}</span>
        <span className="text-xs font-sans text-stone-400">{songs.length} sanger</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {songs.map((song, i) => (
            <SongEntry key={i} song={song} onUpdate={s => updateSong(i, s)} onRemove={() => removeSong(i)} />
          ))}
          <button onClick={addSong} className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary transition-colors mt-1">
            <Plus className="w-3 h-3" /> Add song
          </button>
        </div>
      )}
    </div>
  );
};

export const MusicPlanner = ({ music, onChange, couple }) => {
  const categories = getCategories(couple);
  const updateCategory = (catId, songs) => onChange({ ...music, playlists: { ...music.playlists, [catId]: songs } });

  return (
    <div data-testid="music-planner" className="space-y-4">
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Musikk & Fest</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">Create playlists for each part of the wedding</p>
      </div>

      {/* Playlists */}
      <div className="space-y-2">
        {categories.map(cat => (
          <PlaylistCategory key={cat.id} category={cat}
            songs={music.playlists?.[cat.id] || []}
            onUpdate={songs => updateCategory(cat.id, songs)} />
        ))}
      </div>

      {/* Must / Don't play */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <SongList title="Must play!" icon="text-green-600" songs={music.mustPlay || []}
          onChange={list => onChange({ ...music, mustPlay: list })} placeholder="Songs that absolutely must be included" />
        <SongList title="Don't play!" icon="text-red-500" songs={music.doNotPlay || []}
          onChange={list => onChange({ ...music, doNotPlay: list })} placeholder="Songs you don't want to hear" />
      </div>

      {/* First Dance & Speeches */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <h4 className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide">Highlights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">First dance</label>
            <input value={music.firstDanceSong || ''} onChange={e => onChange({ ...music, firstDanceSong: e.target.value })}
              placeholder="Sang og artist" className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">Toast master</label>
            <input value={music.toastmaster || ''} onChange={e => onChange({ ...music, toastmaster: e.target.value })}
              placeholder="Navn" className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="text-xs font-sans font-medium text-stone-600 mb-1 block">Speeches (hvem og order)</label>
          <textarea value={music.speeches || ''} onChange={e => onChange({ ...music, speeches: e.target.value })}
            placeholder={couple?.person1Name ? `e.g. 1. ${couple.person2Name || 'Person 2'}s father, 2. ${couple.person1Name}s sister, 3. Best man/maid of honor` : 'e.g. 1. Father, 2. Sister, 3. Best man/maid of honor'} rows={3}
            className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
        </div>
        <div className="flex flex-wrap gap-3">
          {['Cake cutting', 'Bukettast', 'Photobooth', 'Guest book', 'Audio guestebok'].map(act => (
            <label key={act} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={(music.activities || []).includes(act)}
                onChange={e => {
                  const list = music.activities || [];
                  onChange({ ...music, activities: e.target.checked ? [...list, act] : list.filter(a => a !== act) });
                }}
                className="rounded border-stone-300 text-primary focus:ring-primary" />
              <span className="text-xs font-sans text-stone-600">{act}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

const SongList = ({ title, icon, songs, onChange, placeholder }) => (
  <div className="bg-white rounded-2xl border border-stone-100 p-4">
    <h4 className={`text-xs font-sans font-bold uppercase tracking-wide mb-2 ${icon}`}>{title}</h4>
    <div className="space-y-1.5">
      {songs.map((song, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={song} onChange={e => { const u = [...songs]; u[i] = e.target.value; onChange(u); }} placeholder={placeholder}
            className="flex-1 text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-stone-50 focus:border-primary focus:outline-none" />
          <button onClick={() => onChange(songs.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-500">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
    <button onClick={() => onChange([...songs, ''])} className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary mt-2">
      <Plus className="w-3 h-3" /> Add
    </button>
  </div>
);
